import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMember, useRoles, useUpdateMemberRole } from '@/hooks/useUsers';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppCache } from '@/contexts/AppCacheContext';
import { useBreadcrumbLabel } from '../contexts/BreadcrumbContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserRoleBadge } from '@/components/users/UserRoleBadge';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { Time } from '@/components/ui/time';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { AlertCircle, Check } from 'lucide-react';

/**
 * User Detail Page
 *
 * Shows user details and allows role management.
 * Only organization owners can change roles.
 */

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser } = useAuth0();
  const { hasPermission } = useAppCache();
  const canUpdateUsers = hasPermission('update:users');

  const { data: member, isLoading, error: queryError } = useMember(userId);
  const { data: availableRoles = [] } = useRoles();
  const updateMemberRoleMutation = useUpdateMemberRole();
  const isUpdating = updateMemberRoleMutation.isPending;
  const error = queryError instanceof Error ? queryError.message : null;

  // Set breadcrumb label to user name
  useBreadcrumbLabel(member?.name || member?.email?.split('@')[0]);

  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Check if current user is viewing themselves
  const isCurrentUser = currentUser?.sub === userId;

  // Get initials for avatar fallback
  const getInitials = (email: string, name?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.charAt(0).toUpperCase();
  };

  // Handle role change
  const handleRoleChange = (newRole: string) => {
    if (newRole !== member?.primaryRole) {
      setSelectedRole(newRole);
      setShowConfirmDialog(true);
    }
  };

  // Confirm role change
  const confirmRoleChange = async () => {
    if (!userId || !selectedRole) return;

    setUpdateError(null);
    try {
      await updateMemberRoleMutation.mutateAsync({ userId, role: selectedRole });
      setSuccessMessage(`Role updated to ${selectedRole}`);
      setShowConfirmDialog(false);
      setSelectedRole(null);

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update role');
      setShowConfirmDialog(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto pt-5 pb-8">
          <Breadcrumbs />
          <EmptyState
            variant="error"
            icon={AlertCircle}
            title="Failed to load user"
            description={error}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto pt-5 pb-8">
        <Breadcrumbs />

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 rounded bg-success/15 p-4 flex items-center gap-3">
            <Check className="h-5 w-5 stroke-[1.5] text-success" />
            <p className="text-sm text-success">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {updateError && (
          <div className="mb-6 rounded bg-destructive/15 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 stroke-[1.5] text-destructive" />
            <div>
              <p className="font-medium text-destructive">Failed to update role</p>
              <p className="text-sm text-muted-foreground">{updateError}</p>
            </div>
          </div>
        )}

        {isLoading ? (
          // Loading skeleton
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
            <div className="bg-surface rounded p-6">
              <Skeleton className="h-6 w-32 mb-8" />
              <div className="space-y-4">
                <Skeleton className="h-4 w-full max-w-md" />
                <Skeleton className="h-4 w-full max-w-sm" />
                <Skeleton className="h-4 w-full max-w-xs" />
              </div>
            </div>
          </div>
        ) : member ? (
          <>
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={member.picture} alt={member.email} />
                        <AvatarFallback className="text-lg">
                          {getInitials(member.email, member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
                        {member.name || member.email.split('@')[0]}
                        {isCurrentUser && (
                          <span className="ml-2 text-sm font-normal text-muted-foreground">
                            (You)
                          </span>
                        )}
                      </h1>
                      <UserRoleBadge role={member.primaryRole} />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 mt-2 text-sm text-muted-foreground">
                    <span>{member.email}</span>
                    <span>·</span>
                    <span>Joined <Time value={member.createdAt} format="medium" /></span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Sections */}
            <div className="space-y-6">
              {/* User Details */}
              <div className="bg-surface rounded p-6">
                <h3 className="text-xl font-medium leading-none mb-8 text-foreground">Overview</h3>
                <dl className="space-y-4">
                  <div className="flex items-baseline">
                    <dt className="text-sm text-muted-foreground w-28 flex-shrink-0">Email</dt>
                    <dd className="text-sm text-foreground">{member.email}</dd>
                  </div>
                  <div className="flex items-center">
                    <dt className="text-sm text-muted-foreground w-28 flex-shrink-0">Role</dt>
                    <dd>
                      <UserRoleBadge role={member.primaryRole} />
                    </dd>
                  </div>
                  <div className="flex items-baseline">
                    <dt className="text-sm text-muted-foreground w-28 flex-shrink-0">Joined</dt>
                    <dd className="text-sm text-foreground"><Time value={member.createdAt} format="medium" /></dd>
                  </div>
                </dl>
              </div>

              {/* Role Management */}
              {canUpdateUsers && !isCurrentUser && member.primaryRole.toLowerCase() !== 'system admin' && (
                <div className="bg-surface rounded p-6">
                  <h3 className="text-xl font-medium leading-none mb-8 text-foreground">Role Management</h3>
                  <dl className="space-y-4">
                    <div className="flex items-baseline">
                      <dt className="text-xs capitalize text-muted-foreground w-28 flex-shrink-0">Change Role</dt>
                      <dd className="flex-1">
                        <Select
                          value={member.primaryRole}
                          onValueChange={handleRoleChange}
                          disabled={isUpdating}
                        >
                          <SelectTrigger className="w-full max-w-xs">
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableRoles.map((role) => (
                              <SelectItem key={role.name} value={role.name}>
                                <div className="flex flex-col">
                                  <span>{role.name}</span>
                                  <span className="text-sm text-muted-foreground">
                                    {role.description}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground mt-2">
                          Select a new role to change this user's permissions
                        </p>
                      </dd>
                    </div>
                  </dl>
                </div>
              )}

              {/* Self-edit warning */}
              {isCurrentUser && (
                <div className="bg-surface rounded p-6">
                  <p className="text-sm text-muted-foreground">
                    You cannot change your own role. Ask another organization owner to update your role.
                  </p>
                </div>
              )}

              {/* System admin warning */}
              {!isCurrentUser && member.primaryRole.toLowerCase() === 'system admin' && canUpdateUsers && (
                <div className="bg-surface rounded p-6">
                  <p className="text-sm text-muted-foreground">
                    System Admin roles can only be managed by other System Admins.
                  </p>
                </div>
              )}
            </div>
          </>
        ) : null}

        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Change user role?</AlertDialogTitle>
              <AlertDialogDescription>
                You are about to change {member?.name || member?.email}'s role from{' '}
                <strong>{member?.primaryRole}</strong> to{' '}
                <strong>{selectedRole}</strong>.
                <br /><br />
                This will immediately change their permissions.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmRoleChange} disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Updating...
                  </>
                ) : (
                  'Confirm'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default UserDetail;
